<!-- Two independent flex columns rather than letting each card be a grid item: as grid items the
     cards share a row height, so the tall Network card left a gap under General. Stacking each
     column separately lets a card start directly below the one above it. -->
<xemex-card-grid>
  <div class="flex flex-col gap-6">
    <xemex-card
      [title]="'devices.detailTabs.general.label' | translate"
      [actionLabel]="showExportCsv() ? ('devices.action.exportCsv' | translate) : undefined"
      actionIcon="file_download"
      (action)="exportCsvClicked.emit()"
    >
      <div class="flex flex-col gap-4">
        <xemex-detail-field
          [label]="'devices.detailTabs.general.deviceInstallationStatus' | translate"
          [loading]="isLoading()"
        >
          {{
            installationStatusKey()
              ? (installationStatusKey()! | translate)
              : deviceDetail()?.deviceInstallationStatus
          }}
        </xemex-detail-field>
        @if (deviceDetail()?.installationFailureReason) {
          <xemex-detail-field
            data-testid="installation-failure-reason"
            [label]="'devices.detailTabs.general.installationFailureReason' | translate"
            [loading]="isLoading()"
          >
            {{
              failureReasonKey()
                ? (failureReasonKey()! | translate)
                : deviceDetail()?.installationFailureReason
            }}
          </xemex-detail-field>
        }
        <xemex-detail-field
          [label]="'devices.detailTabs.general.lastReadout' | translate"
          [loading]="isLoading()"
        >
          {{
            (deviceDetail()?.lastReadout
              ? $safeNavigationMigration(deviceDetail()?.lastReadout) + 'Z'
              : ''
            ) | date: 'HH:mm:ss dd MMM yyyy'
          }}
        </xemex-detail-field>
      </div>
    </xemex-card>

    <xemex-card
      [title]="'devices.detailTabs.location.label' | translate"
      [actionLabel]="locationRes() ? 'More' : undefined"
      [loading]="isLoading() || (!!deviceDetail()?.locationId && !locationRes())"
      actionIcon="chevron_right"
      (action)="onLocationClick($safeNavigationMigration(locationRes()?.locationId)!)"
    >
      @if (locationRes()) {
        <div class="flex flex-col gap-4">
          <xemex-detail-field [label]="'devices.detailTabs.location.address' | translate">
            {{ locationRes()?.locationStreet }} {{ locationRes()?.locationStreetNumber
            }}{{
              locationRes()?.locationStreetNumberAddition
                ? ' ' + locationRes()?.locationStreetNumberAddition
                : ''
            }}
          </xemex-detail-field>
          <xemex-detail-field [label]="'devices.detailTabs.location.postalCode' | translate">
            {{ locationRes()?.locationPostalCode }}
          </xemex-detail-field>
          <xemex-detail-field [label]="'devices.detailTabs.location.city' | translate">
            {{ locationRes()?.locationCity }}
          </xemex-detail-field>
          @if (showInstallations()) {
            <xemex-detail-field [label]="'devices.detailTabs.location.installations' | translate">
              @for (installation of fedInstallations(); track installation.installationOid) {
                <a
                  class="block text-text-link underline"
                  [routerLink]="[
                    '/',
                    APP_PATHS.Installations,
                    installation.installationOid,
                    'general',
                  ]"
                  >{{ installation.installationName }}</a
                >
              } @empty {
                –
              }
            </xemex-detail-field>
          }
        </div>
      }
    </xemex-card>

    @if (showInstallationGroups()) {
      <xemex-card
        [title]="'devices.detailTabs.installationGroups.label' | translate"
        [loading]="installationGroupsLoading()"
      >
        @if (!installationGroups().length) {
          <div class="flex justify-center items-center py-6">
            <img src="assets/images/empty/document.svg" alt="" />
          </div>
        } @else {
          <div class="flex flex-col divide-y divide-gray-100">
            @for (group of installationGroups(); track group.installationGroupOid) {
              <div
                class="flex items-center justify-between py-2.5 cursor-pointer hover:bg-gray-50 -mx-1 px-1 rounded"
                (click)="installationGroupClicked.emit(group.installationGroupOid)"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-none"
                  >
                    <mat-icon class="text-gray-500 !text-sm !flex !items-center !justify-center">
                      workspaces
                    </mat-icon>
                  </div>
                  <div>
                    <div class="text-sm font-semibold">{{ group.name }}</div>
                    <div class="text-xs text-gray-500">{{ group.ownerPartnerName }}</div>
                  </div>
                </div>
                <mat-icon class="text-gray-400 !text-lg">chevron_right</mat-icon>
              </div>
            }
          </div>
        }
      </xemex-card>
    }

    <xemex-card
      [title]="'devices.detailTabs.company.label' | translate"
      [actionLabel]="partnerRes() ? 'More' : undefined"
      [loading]="isLoading() || !partnerRes()"
      actionIcon="chevron_right"
      (action)="onClickCompany($safeNavigationMigration(partnerRes()?.partnerOid)!)"
    >
      @if (partnerRes()) {
        <xemex-detail-field [label]="'devices.detailTabs.company.name' | translate">
          {{ partnerRes()?.partnerName }}
        </xemex-detail-field>
      }
    </xemex-card>
  </div>

  <div class="flex flex-col gap-6">
    @if (showNetworkCard()) {
      <xemex-card
        [title]="'devices.detailTabs.network.label' | translate"
        [actionLabel]="
          showConnectionTest() ? ('devices.action.connectionTest' | translate) : undefined
        "
        actionIcon="network_check"
        actionIconPosition="start"
        (action)="connectionTestClicked.emit()"
      >
        @if (!isLoading() && hasSim()) {
          <button
            cardHeaderActions
            mat-icon-button
            [matMenuTriggerFor]="simMenu"
            [attr.aria-label]="'devices.detailTabs.sim.actionsMenu' | translate"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
        }

        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between gap-2">
            <xemex-inline-heading>{{
              'devices.detailTabs.sim.label' | translate
            }}</xemex-inline-heading>
            @if (!isLoading() && deviceDetail()?.simLastSyncedAt; as lastSyncedAt) {
              <span class="flex items-center gap-1 text-body-xs text-text-muted">
                <mat-icon class="!h-4 !w-4 !text-base !leading-4" fontSet="material-icons-outlined">
                  sync
                </mat-icon>
                {{
                  'devices.detailTabs.sim.syncedAt'
                    | translate: { datetime: lastSyncedAt | date: 'dd MMM yyyy, HH:mm' }
                }}
              </span>
            }
          </div>

          @if (!isLoading() && !hasSim()) {
            <div class="flex flex-col items-center justify-center gap-2 py-6">
              <mat-icon class="text-text-muted" fontSet="material-icons-outlined"
                >sim_card_alert</mat-icon
              >
              <p class="text-sm text-text-muted">
                {{ 'devices.detailTabs.sim.noSimInfo' | translate }}
              </p>
            </div>
          } @else {
            <xemex-detail-field
              [label]="'devices.detailTabs.sim.simStatus' | translate"
              [loading]="isLoading() || isRefreshingSim()"
            >
              @if (deviceDetail()?.simStatus; as simStatus) {
                <xemex-status-chip
                  [status]="mapDeviceStatusToBadgeStatus(simStatus)"
                  [label]="simStatus"
                  dot
                />
              }
            </xemex-detail-field>

            <div class="grid grid-cols-2 gap-4">
              <xemex-detail-field
                [label]="'devices.detailTabs.sim.dataRemaining' | translate"
                [loading]="isLoading() || isRefreshingSim()"
              >
                @if (simData(); as data) {
                  <span class="flex flex-col gap-2">
                    <span class="whitespace-nowrap">
                      <span class="text-body-md font-semibold">{{ data.remainingLabel }}</span>
                      <span class="text-body-xs text-text-secondary"> / {{ data.totalLabel }}</span>
                    </span>
                    <xemex-progress-bar
                      [value]="data.remainingMb"
                      [max]="data.totalMb"
                      [ariaLabel]="'devices.detailTabs.sim.dataRemaining' | translate"
                    />
                  </span>
                }
              </xemex-detail-field>

              <xemex-detail-field
                [label]="'devices.detailTabs.sim.lifetime' | translate"
                [loading]="isLoading() || isRefreshingSim()"
              >
                @if (simLifetime(); as lifetime) {
                  <span class="flex flex-col gap-2">
                    <span class="whitespace-nowrap">
                      <span class="text-body-md font-semibold">{{
                        lifetime.remaining.key | translate: lifetime.remaining.params
                      }}</span>
                      @if (!lifetime.expired) {
                        <span class="text-body-xs text-text-secondary">
                          {{ 'devices.detailTabs.sim.lifetimeLeft' | translate }}</span
                        >
                      }
                    </span>
                    <xemex-progress-bar
                      [value]="lifetime.remainingDays"
                      [max]="lifetime.totalDays"
                      [startLabel]="lifetime.activationDate | date: 'dd MMM yyyy'"
                      [endLabel]="
                        'devices.detailTabs.sim.lifetimeEnds'
                          | translate: { date: lifetime.endDate | date: 'dd MMM yyyy' }
                      "
                      [ariaLabel]="'devices.detailTabs.sim.lifetime' | translate"
                    />
                  </span>
                }
              </xemex-detail-field>
            </div>

            <xemex-detail-field
              [label]="'devices.detailTabs.sim.serialNumber' | translate"
              [loading]="isLoading()"
            >
              {{ deviceDetail()?.simIccid }}
            </xemex-detail-field>
          }

          @if (deviceDetail()?.lanIpaddress) {
            <div class="border-t border-border-default"></div>
            <xemex-inline-heading>{{
              'devices.detailTabs.lan.label' | translate
            }}</xemex-inline-heading>
            <xemex-detail-field
              [label]="'devices.detailTabs.lan.ipAddress' | translate"
              [loading]="isLoading()"
            >
              {{ deviceDetail()?.lanIpaddress }}
            </xemex-detail-field>
          }
        </div>

        <mat-menu #simMenu="matMenu" xPosition="before">
          @if (isXemex()) {
            <button
              mat-menu-item
              class="menu-item--stacked"
              [disabled]="isTopUpInProgress() || isExtendInProgress()"
              (click)="onTopUpSimClick()"
            >
              <mat-icon fontSet="material-icons-outlined">add_card</mat-icon>
              <span class="flex flex-col">
                <span>{{ 'devices.action.topUpSim' | translate: { volume: topUpVolumeMb } }}</span>
                <span class="text-body-xs text-text-muted">{{
                  'devices.action.topUpSimHint' | translate
                }}</span>
              </span>
            </button>
            <button
              mat-menu-item
              class="menu-item--stacked"
              [disabled]="isExtendInProgress() || isTopUpInProgress()"
              (click)="onExtendSimClick()"
            >
              <mat-icon fontSet="material-icons-outlined">event_repeat</mat-icon>
              <span class="flex flex-col">
                <span>{{ 'devices.action.extendSim' | translate }}</span>
                <span class="text-body-xs text-text-muted">{{
                  'devices.action.extendSimHint' | translate
                }}</span>
              </span>
            </button>
            <mat-divider />
          }
          <button mat-menu-item [disabled]="isRefreshingSim()" (click)="onRefreshSimClick()">
            <mat-icon fontSet="material-icons-outlined">sync</mat-icon>
            <span>{{ 'devices.action.refreshSim' | translate }}</span>
          </button>
        </mat-menu>
      </xemex-card>
    }

    <xemex-card
      [title]="'devices.detailTabs.firmware.label' | translate"
      [actionLabel]="
        showFirmwareUpdate() ? ('devices.detailTabs.firmware.updateAction' | translate) : undefined
      "
      actionIcon="system_update_alt"
      (action)="firmwareUpdateClicked.emit()"
    >
      <xemex-detail-field
        [label]="'devices.detailTabs.general.deviceFirmwareVersion' | translate"
        [loading]="isLoading()"
      >
        {{ deviceDetail()?.deviceFirmwareVersion }}
      </xemex-detail-field>
    </xemex-card>
  </div>
</xemex-card-grid>
